import { useState, useEffect, useMemo } from "react";
import { Transition } from "@headlessui/react";
import axios from "axios";
import UserForm from "./UserForm";
import RipplingSyncButton from "./RipplingSyncButton";
const API = "https://erp-xe9u.onrender.com/api";

const ROLE_LABELS = {
  admin: "Admin",
  foreman: "Foreman",
  project_manager: "PM",
  site_worker: "Site Worker",
  estimator: "Estimator",
  unassigned: "Unassigned",
};

const ROLE_COLORS = {
  admin: "bg-orange-400/15 text-orange-300 ring-orange-400/30",
  foreman: "bg-blue-400/15 text-blue-300 ring-blue-400/30",
  project_manager: "bg-purple-400/15 text-purple-300 ring-purple-400/30",
  site_worker: "bg-teal-400/15 text-teal-300 ring-teal-400/30",
  estimator: "bg-yellow-400/15 text-yellow-300 ring-yellow-400/30",
  unassigned: "bg-gray-400/15 text-gray-400 ring-gray-400/30",
};

const CLASSIFICATION_COLORS = {
  apprentice: "bg-sky-400/10 text-sky-300 ring-sky-400/20",
  journeyman: "bg-cyan-400/10 text-cyan-300 ring-cyan-400/20",
  foreman: "bg-indigo-400/10 text-indigo-300 ring-indigo-400/20",
};

// ─── Add these helpers near the top, after CLASSIFICATION_COLORS ─────────────

const SafetyBadge = ({ user }) => {
  const { isSafeToWork, safetyDetails } = user;

  // No docs at all
  if (!safetyDetails?.activeCount && !safetyDetails?.activeValidCount) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold
                       bg-gray-400/10 text-gray-400 ring-1 ring-inset ring-gray-400/20 whitespace-nowrap"
      >
        <svg
          className="w-2.5 h-2.5 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        No docs
      </span>
    );
  }

  if (isSafeToWork) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold
                       bg-green-400/10 text-green-400 ring-1 ring-inset ring-green-400/20 whitespace-nowrap"
      >
        <svg
          className="w-2.5 h-2.5 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
        Safe
      </span>
    );
  }

  // Has docs but some expired
  if (
    safetyDetails?.activeExpiredCount > 0 ||
    safetyDetails?.expiredCount > 0
  ) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold
                       bg-red-400/10 text-red-400 ring-1 ring-inset ring-red-400/20 whitespace-nowrap"
      >
        <svg
          className="w-2.5 h-2.5 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v4m0 4h.01"
          />
        </svg>
        Not safe
      </span>
    );
  }

  // Mandatory docs missing
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold
                     bg-amber-400/10 text-amber-300 ring-1 ring-inset ring-amber-400/20 whitespace-nowrap"
    >
      <svg
        className="w-2.5 h-2.5 shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        />
      </svg>
      Review
    </span>
  );
};

const isMissingPayRate = (user) =>
  Array.isArray(user.payType) &&
  user.payType.includes("hourly") &&
  (!user.payRate || user.payRate === 0);

const isMissingEmployeeNumber = (user) =>
  !user.employeeNumber || user.employeeNumber.trim() === "";

const MissingBadge = ({ label }) => (
  <span
    title={`Missing: ${label}`}
    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold
               bg-amber-400/10 text-amber-300 ring-1 ring-inset ring-amber-400/25 whitespace-nowrap"
  >
    <svg
      className="w-2.5 h-2.5 shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
      />
    </svg>
    {label}
  </span>
);

export default function UserList({
  users,
  setUsers,
  stagedUsers,
  setStagedUsers,
  accounts,
  unions,
  setDialog,
  navigate,
  isAddingUser,
  setIsAddingUser,
  currentUserPage,
  setCurrentUserPage,
  currentUser,
  onRefresh,
}) {
  const [laborRates, setLaborRates] = useState([]);
  const [userFilter, setUserFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [usersPerPage] = useState(25);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [showUnsafeOnly, setShowUnsafeOnly] = useState(false);
  const isProjectManager = currentUser?.role === "project_manager";

  // Fetch labor rate classifications
  useEffect(() => {
    const fetchLaborRates = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/company-profile/labor-rates`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLaborRates(
          Array.isArray(res.data.laborRates) ? res.data.laborRates : [],
        );
      } catch (err) {
        console.error("Failed to fetch labor rates:", err);
        setLaborRates([]);
      }
    };
    fetchLaborRates();
  }, []);

  const filteredUsers = useMemo(() => {
    let pool = stagedUsers;

    if (isProjectManager) {
      pool = pool.filter(
        (u) =>
          u.role === "site_worker" ||
          u.role === "foreman" ||
          u._id === currentUser._id,
      );
    }

    switch (userFilter) {
      case "active":
        pool = pool.filter((u) => u.active);
        break;
      case "inactive":
        pool = pool.filter((u) => !u.active);
        break;
      case "temp":
        pool = pool.filter((u) => u.isTemporary);
        break;
      case "non-temp":
        pool = pool.filter((u) => !u.isTemporary);
        break;
      default:
        break;
    }

    if (showFlaggedOnly) {
      pool = pool.filter(
        (u) => isMissingPayRate(u) || isMissingEmployeeNumber(u),
      );
    }
    if (showUnsafeOnly) {
      pool = pool.filter((u) => !u.isSafeToWork);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      pool = pool.filter(
        (u) =>
          u.displayName?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.employeeNumber?.toLowerCase().includes(q) ||
          u.role?.toLowerCase().includes(q) ||
          u.phoneNumber?.toLowerCase().includes(q) ||
          u.workerClassification?.toLowerCase().includes(q),
      );
    }

    return pool;
  }, [
    stagedUsers,
    userFilter,
    searchQuery,
    showFlaggedOnly,
    showUnsafeOnly,
    isProjectManager,
    currentUser,
  ]);
  const paginatedUsers = useMemo(() => {
    const start = (currentUserPage - 1) * usersPerPage;
    return filteredUsers.slice(start, start + usersPerPage);
  }, [filteredUsers, currentUserPage, usersPerPage]);

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const flaggedCount = useMemo(
    () =>
      stagedUsers.filter(
        (u) => isMissingPayRate(u) || isMissingEmployeeNumber(u),
      ).length,
    [stagedUsers],
  );
  // Add after flaggedCount useMemo:
  const unsafeCount = useMemo(
    () => stagedUsers.filter((u) => !u.isSafeToWork).length,
    [stagedUsers],
  );

  const handleViewUser = (userId) => navigate(`/users/${userId}`);

  const handleStartEditUser = (user) => {
    setEditingUser({
      ...user,
      account: user.account?._id || null,
      payType: user.payType || [],
      hoursPerWeek: user.hoursPerWeek || null,
      collectsTax: user.collectsTax || false,
      taxRate: user.taxRate || 0,
      wsib: user.wsib !== undefined ? user.wsib : true,
      phoneNumber: user.phoneNumber || "",
      workerClassification: user.workerClassification || null,
    });
    setIsEditingUser(true);
  };

  const filterOptions = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "temp", label: "Temporary" },
    { value: "non-temp", label: "Non-Temp" },
  ];

  return (
    <>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search by name, email, role, classification…"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentUserPage(1);
            }}
            className="w-full pl-9 pr-4 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg
                       text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-1
                       focus:ring-orange-400 focus:border-orange-400 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isProjectManager && <RipplingSyncButton onSynced={onRefresh} />}

          {flaggedCount > 0 && (
            <button
              onClick={() => {
                setShowFlaggedOnly((v) => !v);
                setCurrentUserPage(1);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                showFlaggedOnly
                  ? "bg-amber-400/15 text-amber-300 border-amber-400/30"
                  : "bg-gray-800 text-amber-400 border-amber-400/20 hover:bg-amber-400/10"
              }`}
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
              {flaggedCount} needs attention
            </button>
          )}

          {unsafeCount > 0 && (
            <button
              onClick={() => {
                setShowUnsafeOnly((v) => !v);
                setCurrentUserPage(1);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                showUnsafeOnly
                  ? "bg-red-400/15 text-red-300 border-red-400/30"
                  : "bg-gray-800 text-red-400 border-red-400/20 hover:bg-red-400/10"
              }`}
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v4m0 4h.01"
                />
              </svg>
              {unsafeCount} not safe
            </button>
          )}
          <div className="flex items-center gap-1 bg-gray-800 border border-gray-700 rounded-lg p-1">
            {filterOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  setUserFilter(opt.value);
                  setCurrentUserPage(1);
                }}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                  userFilter === opt.value
                    ? "bg-orange-400 text-white shadow-sm"
                    : "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-3">
        {filteredUsers.length === 0
          ? "No users match your filters"
          : `Showing ${paginatedUsers.length} of ${filteredUsers.length} user${filteredUsers.length !== 1 ? "s" : ""}`}
        {searchQuery && (
          <span className="text-orange-400"> · "{searchQuery}"</span>
        )}
        // With:
        {showFlaggedOnly && (
          <span className="text-amber-400"> · flagged only</span>
        )}
        {showUnsafeOnly && (
          <span className="text-red-400"> · not safe only</span>
        )}
      </p>

      {/* Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-lg">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Name
              </th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Role
              </th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">
                Pay Types
              </th>
              {currentUser?.role === "admin" && (
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  Pay Rate
                </th>
              )}
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                Emp #
              </th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                Safety
              </th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider hidden xl:table-cell">
                Last Login
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/60">
            {paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-5 py-16 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <svg
                      className="w-10 h-10 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <p className="text-sm text-gray-500">No users found</p>
                    {(searchQuery || showFlaggedOnly) && (
                      <button
                        onClick={() => {
                          setSearchQuery("");
                          setShowFlaggedOnly(false);
                        }}
                        className="text-xs text-orange-400 hover:text-orange-300 transition-colors"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => {
                const missingRate = isMissingPayRate(user);
                const missingEmpNo = isMissingEmployeeNumber(user);
                const hasMissing = missingRate || missingEmpNo;

                return (
                  <tr
                    key={user._id}
                    onClick={() => handleViewUser(user._id)}
                    className={`group cursor-pointer transition-colors hover:bg-gray-700/60 ${!user.active ? "opacity-50" : ""} ${hasMissing ? "bg-amber-400/[0.03]" : ""}`}
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-700 border border-gray-600 flex items-center justify-center shrink-0 text-xs font-semibold text-gray-300 uppercase mt-0.5">
                          {user.displayName?.charAt(0) || "?"}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-sm font-medium text-gray-100 leading-tight">
                              {user.displayName}
                            </p>
                            {user.isTemporary && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-yellow-400/10 text-yellow-300 ring-1 ring-inset ring-yellow-400/20">
                                Temp
                              </span>
                            )}
                            {user.workerClassification && (
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ring-inset capitalize ${CLASSIFICATION_COLORS[user.workerClassification] || "bg-cyan-400/10 text-cyan-300 ring-cyan-400/20"}`}
                              >
                                {user.workerClassification}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 leading-tight mt-0.5">
                            {user.email}
                          </p>
                          {hasMissing && (
                            <div className="flex gap-1 mt-1.5 lg:hidden flex-wrap">
                              {missingRate && currentUser?.role === "admin" && (
                                <MissingBadge label="Pay rate" />
                              )}
                              <td className="px-5 py-3.5 hidden lg:table-cell">
                                {missingEmpNo ? (
                                  <MissingBadge label="Not set" />
                                ) : (
                                  <span className="text-xs font-mono text-gray-300 bg-gray-700/60 px-2 py-0.5 rounded">
                                    {user.employeeNumber}
                                  </span>
                                )}
                                {user.ripplingEmployeeNumber && (
                                  <span className="block mt-1 text-[10px] font-mono text-orange-300/80">
                                    R: {user.ripplingEmployeeNumber}
                                  </span>
                                )}
                              </td>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ring-1 ring-inset ${ROLE_COLORS[user.role] || ROLE_COLORS.unassigned}`}
                      >
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </td>

                    <td className="px-5 py-3.5 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {user.payType?.length > 0 ? (
                          user.payType.map((pt) => (
                            <span
                              key={pt}
                              className="px-1.5 py-0.5 rounded text-xs bg-gray-700 text-gray-300 border border-gray-600 capitalize"
                            >
                              {pt}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-600">—</span>
                        )}
                      </div>
                    </td>
                    {currentUser?.role === "admin" && (
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        {missingRate ? (
                          <MissingBadge label="No rate set" />
                        ) : user.payRate ? (
                          <span className="text-sm font-medium text-gray-200">
                            ${Number(user.payRate).toFixed(2)}
                            <span className="text-xs text-gray-500">/hr</span>
                          </span>
                        ) : (
                          <span className="text-xs text-gray-600">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-5 py-3.5 hidden lg:table-cell">
                      {missingEmpNo ? (
                        <MissingBadge label="Not set" />
                      ) : (
                        <span className="text-xs font-mono text-gray-300 bg-gray-700/60 px-2 py-0.5 rounded">
                          {user.employeeNumber}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`w-1.5 h-1.5 rounded-full shrink-0 ${user.active ? "bg-green-400" : "bg-red-400"}`}
                        />
                        <span
                          className={`text-xs font-medium ${user.active ? "text-green-400" : "text-red-400"}`}
                        >
                          {user.active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 hidden sm:table-cell">
                      <SafetyBadge user={user} />
                    </td>
                    <td className="px-5 py-3.5 hidden xl:table-cell">
                      <span className="text-xs text-gray-500">
                        {user.lastLogin
                          ? new Date(user.lastLogin).toLocaleDateString()
                          : "Never"}
                      </span>
                    </td>
                    <td
                      className="px-5 py-3.5 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleViewUser(user._id)}
                          className="px-2.5 py-1 text-xs font-medium text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-md transition-colors border border-gray-600"
                        >
                          View
                        </button>
                        {!isProjectManager && (
                          <button
                            onClick={() => handleStartEditUser(user)}
                            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors border ${
                              hasMissing
                                ? "text-amber-300 hover:text-white bg-amber-400/10 hover:bg-amber-400/20 border-amber-400/20"
                                : "text-orange-300 hover:text-white bg-orange-400/10 hover:bg-orange-400/20 border-orange-400/20"
                            }`}
                          >
                            {hasMissing ? "Complete" : "Edit"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">
            Page {currentUserPage} of {totalPages}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentUserPage((p) => Math.max(1, p - 1))}
              disabled={currentUserPage === 1}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ← Prev
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const start = Math.max(
                1,
                Math.min(currentUserPage - 2, totalPages - 4),
              );
              const page = start + i;
              return page <= totalPages ? (
                <button
                  key={page}
                  onClick={() => setCurrentUserPage(page)}
                  className={`w-8 h-8 text-xs font-medium rounded-md transition-colors ${
                    currentUserPage === page
                      ? "bg-orange-400 text-white border border-orange-400"
                      : "border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700"
                  }`}
                >
                  {page}
                </button>
              ) : null;
            })}
            <button
              onClick={() =>
                setCurrentUserPage((p) => Math.min(totalPages, p + 1))
              }
              disabled={currentUserPage === totalPages}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* User Form Modal */}
      <Transition
        show={isAddingUser || isEditingUser}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <UserForm
          isEditing={isEditingUser}
          isViewOnly={isProjectManager}
          user={isEditingUser ? editingUser : null}
          setIsEditing={setIsEditingUser}
          setIsAdding={setIsAddingUser}
          accounts={accounts}
          unions={unions}
          setUsers={setUsers}
          setStagedUsers={setStagedUsers}
          setDialog={setDialog}
          laborRates={laborRates}
        />
      </Transition>
    </>
  );
}
